import React, { useState, useEffect } from 'react';
import {
    Grid,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Autocomplete,
    CircularProgress,
    InputAdornment,
    IconButton,
    Tooltip,
    Box
} from '@mui/material';
import SectionTitle from '../../../ui/sectionTitle';
import { Add, Search } from '@mui/icons-material';
import { SelectLocation } from '../../../common';
import { ValidDataService } from '../../../../service/validDataService';
import { toast } from 'react-toastify';

function ValidAccessoryBasicInfo({
    formik,
    setSupplierDialogOpen,
    setSupplierCreateDialogOpen
}) {
    const [suppliers, setSuppliers] = useState([]);
    const [loadingSuppliers, setLoadingSuppliers] = useState(false);
    
    const updateLocation = (location) => {
        formik.setFieldValue('location', location);
    };

    // Load suppliers on mount
    useEffect(() => {
        loadSuppliers();
    }, []);

    // Load brands when supplier changes
        // useEffect(() => {
        //     if (formik.values.supplierId) {
        //         loadBrands(formik.values.supplierId);
        //     } else {
        //         setBrands([]);
        //         formik.setFieldValue('brand', '');
        //     }
        //     // eslint-disable-next-line react-hooks/exhaustive-deps
        // }, [formik.values.supplierId]);

    const loadSuppliers = async () => {
        setLoadingSuppliers(true);
        try {
            const response = await ValidDataService.getAllSuppliers('accessories');
            if (response.code === 0 && response.data) {
                setSuppliers(response.data);
            }
        } catch (error) {
            console.error('Error loading suppliers:', error);
            toast.error('Failed to load suppliers');
        } finally {
            setLoadingSuppliers(false);
        }
    };

    // const loadBrands = async (supplierId) => {
    //     setLoadingBrands(true);
    //     try {
    //         const response = await ValidDataService.getBrandsForSupplier(supplierId);
    //         if (response.code === 0 && response.data) {
    //             setBrands(response.data);
    //         }
    //     } catch (error) {
    //         console.error('Error loading brands:', error);
    //         toast.error('Failed to load brands');
    //     } finally {
    //         setLoadingBrands(false);
    //     }
    // };

    const handleSupplierChange = (event, value) => {
        if (value) {
            formik.setFieldValue('supplierId', value.id);
            formik.setFieldValue('supplierName', value.supplierName);
        } else {
            formik.setFieldValue('supplierId', '');
            formik.setFieldValue('supplierName', '');
        }
        // Reset brand when supplier changes
        formik.setFieldValue('brand', '');
        formik.setFieldValue('brandId', null);
    };


    return (
        <>
            <Grid item xs={12}>
                <SectionTitle title="Basic Information" />
            </Grid>

            {/* Supplier - Required */}
            <Grid item xs={12} sm={4} mt={1}>
                <Autocomplete
                    id="supplier"
                    options={suppliers}
                    getOptionLabel={(option) => option.supplierName || ''}
                    loading={loadingSuppliers}
                    value={
                        suppliers.find((s) => s.id === formik.values.supplierId)
                        || (formik.values.supplierId ? {
                            id: formik.values.supplierId,
                            supplierName: formik.values.supplierName || ''
                        } : null)
                    }
                    onChange={handleSupplierChange}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label="Supplier *"
                            size="small"
                            error={formik.touched.supplierId && Boolean(formik.errors.supplierId)}
                            helperText={(formik.touched.supplierId && formik.errors.supplierId) || 'Select or add supplier'}
                            InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                    <>
                                        <InputAdornment position="end">
                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                {setSupplierDialogOpen && (
                                                    <Tooltip title="Select supplier">
                                                        <IconButton
                                                            onClick={() => setSupplierDialogOpen(true)}
                                                            size="small"
                                                        >
                                                            <Search />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                                {setSupplierCreateDialogOpen && (
                                                    <Tooltip title="Add supplier">
                                                        <IconButton
                                                            onClick={() => setSupplierCreateDialogOpen()}
                                                            size="small"
                                                        >
                                                            <Add />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                            </Box>
                                        </InputAdornment>
                                        {loadingSuppliers ? <CircularProgress color="inherit" size={20} /> : null}
                                        {params.InputProps.endAdornment}
                                    </>
                                ),
                            }}
                        />
                    )}
                />
            </Grid>

            {/* Model/Product Number */}
            <Grid item xs={12} sm={4} mt={1}>
                <TextField
                    id="modelNumber"
                    name="modelNumber"
                    label={"Name/Product Number *"}
                    size="small"
                    fullWidth
                    value={formik.values.modelNumber}
                    onChange={formik.handleChange}
                    error={Boolean(formik.errors.modelNumber)}
                    helperText={formik.errors.modelNumber}
                />
            </Grid>

            {/* SKU */}
            <Grid item xs={12} sm={3} mt={1}>
                <TextField
                    id="sku"
                    name="sku"
                    label="SKU"
                    size="small"
                    fullWidth
                    onBlur={formik.handleBlur}
                    value={formik.values.sku}
                    onChange={formik.handleChange}
                    error={formik.touched.sku && Boolean(formik.errors.sku)}
                    helperText={formik.touched.sku && formik.errors.sku}
                />
            </Grid>

            {/* Internal UPC */}
            <Grid item xs={12} sm={2} mt={1}>
                <TextField
                    id="internalUpc"
                    name="internalUpc"
                    label="Internal UPC"
                    size="small"
                    fullWidth
                    onBlur={formik.handleBlur}
                    value={formik.values.internalUpc}
                    onChange={formik.handleChange}
                    error={formik.touched.internalUpc && Boolean(formik.errors.internalUpc)}
                    helperText={formik.touched.internalUpc && formik.errors.internalUpc}
                />
            </Grid>

            {/* Supplier UPC */}
            <Grid item xs={12} sm={2} mt={1}>
                <TextField
                    id="supplierUpc"
                    name="supplierUpc"
                    label="Supplier UPC"
                    size="small"
                    fullWidth
                    onBlur={formik.handleBlur}
                    value={formik.values.supplierUpc}
                    onChange={formik.handleChange}
                    error={formik.touched.supplierUpc && Boolean(formik.errors.supplierUpc)}
                    helperText={formik.touched.supplierUpc && formik.errors.supplierUpc}
                />
            </Grid>

            {/* Location */}
            <Grid item xs={12} sm={2} mt={1} mr={2}>
                <SelectLocation 
                    fullWidth={true} 
                    location={formik.values.location.id || ''} 
                    error={Boolean(formik.errors.location)} 
                    required={true}
                    helperText={formik.errors.location} 
                    setLocation={updateLocation}
                />
            </Grid>

            {/* Status */}
            <Grid item xs={12} sm={2} mt={1}>
                <FormControl fullWidth>
                    <InputLabel id="status-label">Status</InputLabel>
                    <Select
                        id="status"
                        name="status"
                        label="Status"
                        size="small"
                        value={formik.values.status}
                        onChange={formik.handleChange}
                        error={formik.touched.status && Boolean(formik.errors.status)}
                    >
                        <MenuItem value="active">Active</MenuItem>
                        <MenuItem value="inactive">Inactive</MenuItem>
                    </Select>
                </FormControl>
            </Grid>
        </>
    );
}

export default ValidAccessoryBasicInfo;
